import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { DataGrid, GridColDef, GridRowClassNameParams } from "@mui/x-data-grid"
import { Block, Add, Edit } from "@mui/icons-material"
import {
  MenuItem,
  Select,
  FormControl, InputLabel,
  Box,
  TextField,
  Divider,
  Button as PriButton
} from "@mui/material"
import {
  Wrapper,
  HeaderWrapper,
    Header,
    Subheader,
} from "../SettingsSection/SettingsSection.styles"
import { openInvoices, selectLGInternalUser } from "../../activeScreenSlice"
import Button from "../Button/Button"
import ValidationRuleLineItem from "../../components/ValidationRulesLineItem/ValidationRuleLineItem"
import { useDownloadValidationConfigTemplate } from '../../hooks/ValidationRules'
import { usePostValidationConfigValues } from '../../hooks/OpCo'
import useShowToast from "../../hooks/useShowToast"
import useShowError from "../../hooks/useShowError"
import { useAppDispatch, useAppSelector } from "../../app/hooks"

type ValidationRule = {
  opco_id: string;
  validation_id: number | null;
  configValue: string;
  files: {
    filename: string;
    path: string;
  };
};

const initialRules: ValidationRule[] = [
  {
    opco_id: "",
    validation_id: null,
    configValue: "",
    files: { filename: "", path: "" }
  }
];

export default function ValidationConfig({opcosList}) {
    const [selectedOption, setSelectedOption] = useState(null);
    const downloadValRuleTemplateMutation = useDownloadValidationConfigTemplate();
    const [rules, setRules] = useState([]);
    const postValConfigDataMutation = usePostValidationConfigValues();
    const showError = useShowError();
    const showToast = useShowToast();

    const [validationIds, setValidationIds] = useState([]);
    const [configValues, setConfigValues] = useState([]);
    const [files, setFiles] = useState([]);
    const [resetTrigger, setResetTrigger] = useState(false);
    const dispatch = useAppDispatch();
    const [isValidationError , setIsValidationError] = useState(false)
    const LGInternalUser = useAppSelector(selectLGInternalUser);

    const DropdownMenu = ({ options }) => {
        // State to store the selected option object


        // Handler to update state when a new option is selected
        const handleChange = (event) => {
            const selectedOpcoName = event.target.value;
            const selectedObj = options.find(option => option.opco_name === selectedOpcoName);
            setSelectedOption(selectedObj);
            setConfigValues([]);
            setFiles([]);
            setValidationIds([]);
            setResetTrigger(prev => !prev);
        };
        return (
            <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{ height: '40px', width: '350px' }}
            >
            <InputLabel id="select-label">Business Unit</InputLabel>
            <Select
                labelId="select-label"
                value={selectedOption ? selectedOption.opco_name : ''}
                onChange={handleChange}
                label="Business Unit"
            >
                {options.map((option) => (
                <MenuItem key={option.opco_id} value={option.opco_name}>
                    {option.opco_name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        );
    };

    const setEmptyArr = (arr) => {
        for (let i = 0; i < arr.length; i++) {
            if (!(i in arr)) {
                arr[i] = undefined;
            }
        }
        return arr;

    }

    const syncArrays = (validationIds, configValues, files) => {
        const targetLength = validationIds.length;

        const extendArray = (arr) => {
            const extended = [...arr];
            while (extended.length < targetLength) {
            extended.push(undefined);
            }
            return extended;
        };

        return {
            validation_ids: validationIds,
            files: extendArray(files),
            config_values: extendArray(configValues),
        };
    };

    const handleSubmit = () => {
        if (isValidationError) {
            showError("Please fix all validation errors before saving.");
            return;
        }
        const synced = syncArrays(setEmptyArr(validationIds), setEmptyArr(configValues), setEmptyArr(files));
        postValConfigDataMutation.mutate({id: selectedOption?.opco_id, payload: synced},
        {
            onSuccess: () => {
                showToast("The validation configuration details for selected business unit have been successfully saved.")
            },
        });
    }


    const handleCancelClick = () => {
        dispatch(openInvoices())
    }


    const handleFileDownloadbyId = (id, name) => {
        const opcoId = selectedOption?.opco_id
        downloadValRuleTemplateMutation.mutate({ validationId: id, opcoId },{
            onSuccess: async (response) => {
                showToast("Template has been successfully downloaded.")
                const blob = response;
                let filename = `${name}.xlsx`;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            },
            onError: () => {
               showError("Error downloading the file from storage.")
            },
        })
    }

    useEffect(() => {
            // Ensure each rule has the correct shape (add defaults if needed)
            const mappedRules = selectedOption?.enabled_config_rules?.map((rule: any) => ({
                opco_id: selectedOption.opco_id ?? "",
                validation_id: rule.id, // comes from DB
                configValue: "",
                files: {
                filename: "",
                path: "",
                },
            }));
            setRules(mappedRules);

        }, [selectedOption]);

     const handleRuleChange = (
        validation_id: number,
        updatedFields: Partial<Omit<ValidationRule, "validation_id">>
    ) => {
        setRules((prevRules) =>
            prevRules.map((rule) =>
                rule.validation_id === validation_id
                ? {
                    ...rule,
                    ...updatedFields,
                    files: { ...rule.files, ...updatedFields.files }
                    }
                : rule
            )
        );
    };

    const handleUpdate = (index, id, value, file) => {

        setValidationIds(prev => {
            const updated = [...prev];
            updated[index] = id;
            return updated;
        });

        if (value !== null && value !== undefined) {
        setConfigValues(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
        }

        if (file !== null && file !== undefined) {
        setFiles(prev => {
            const updated = [...prev];
            updated[index] = file;
            return updated;
        });
        }
    };


        return <Wrapper>
        <Box sx={{ flexDirection: 'row', display: 'flex', gap: 3}}>
            <Box sx={{flexDirection: 'column'}}>
                <HeaderWrapper sx={{mb: 3}}>
                    <Header>Validation rules configuration</Header>
                    <Subheader>
                        Define/edit validation rules
                    </Subheader>
                </HeaderWrapper>

                <Box>
                    <DropdownMenu options={opcosList} />
                </Box>  
                 <Box>
                    <Divider sx={{ my: 3 }} />
                    {
                        selectedOption?.enabled_config_rules?.map((rule, index) => {
                        switch (rule?.validation_rule_identifier.toLowerCase()) {
                            case 'invoice_date_check_past':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Invoice_Date_Check_Past"
                                            ruleId={rule.id}
                                            description="Invoice Date - Must not be older than configured Age"
                                            showAge
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                            setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                        />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'line_items_amount_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Line_Items_Amount_Check"
                                            ruleId={rule.id}
                                            description="Line Amount - Must be equal to Quantity * Unit Price and must ne within  configured tolerance %age when compared to expected amount in PO"
                                            showTolerance
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                                  setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                        />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'supplier_name_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Supplier_Name_Check"
                                            ruleId={rule.id}
                                            description="Supplier name - Must match to Purchase Order supplier name or NON-PO supplier list"
                                            showTolerance
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            showFuzzyLabel
                                            confValue={rule.config_value}
                                                  setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                        />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'receiver_name_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Receiver_Name_Check"
                                            ruleId={rule.id}
                                            description="Receiver Name (Bill-to) - Must match BU bill-to name within configured tolerance as per Fuzzy Logic"
                                            showTolerance
                                            showDownload
                                            showUpload
                                            onUpdate={handleUpdate}
                                            handleDownload= {() => handleFileDownloadbyId(rule.id, "Invoice_Receiver_Whitelist")}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            showFuzzyLabel
                                            confValue={rule.config_value}
                                                  setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                        />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'receiver_po_name':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Receiver_Po_Name"
                                            ruleId={rule.id}
                                            description="Receiver Name PO (Bill-to) - Must match to Purchase Order Bill-to name"
                                            showTolerance
                                            showDownload
                                            showUpload
                                            handleDownload= {() => handleFileDownloadbyId(rule.id, "Entity_Receiver_Name_Mapping")}
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                            showFuzzyLabel
                                                  setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                        />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'non_po_requester_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Non_Po_Requester_Check"
                                            ruleId={rule.id}
                                            description="Requester - Must be an existing requester as per configured list"
                                            showDownload
                                            showUpload
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                            handleDownload= {() => handleFileDownloadbyId(rule.id, "NonPO_Requesters")}
                                             setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                       />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'tax_line_sum_check':
                                return (
                                    <>   
                                        <ValidationRuleLineItem
                                            rule="Tax_Line_Sum_Check"
                                            ruleId={rule.id}
                                            description="Tax Lines - Sum of invoice lines tax values must equal tax total and must be within  configured tolerance %age when compared to expected tax amount in PO"
                                            showTolerance
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                                  setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                        />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'invoice_type_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Invoice_Type_Check"
                                            ruleId={rule.id}
                                            description="Invoice file name should match the patterns, when given"
                                            showDownload
                                            showUpload
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                            handleDownload= {() => handleFileDownloadbyId(rule.id, "Attachment_Filenames")}
                                           setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                     />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'qr_iban_match_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Qr_Iban_Match_Check"
                                            ruleId={rule.id}
                                            description="QR IBAN match - Supplier IBAN from bank details and QR IBAN must match, if the receiver name is in configured list"
                                            showDownload
                                            showUpload
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            confValue={rule.config_value}
                                            reset={resetTrigger}
                                            handleDownload= {() => handleFileDownloadbyId(rule.id, "IBAN_Receivers")}
                                            setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                      />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            case 'spot_rate_check':
                                return (
                                    <>
                                        <ValidationRuleLineItem
                                            rule="Spot_Rate_Check"
                                            ruleId={rule.id}
                                            description="Spot Rate - Must be in valid currency and valid decimal number for flagged suppliers"
                                            showDownload
                                            onUpdate={handleUpdate}
                                            key={rule.id}
                                            index={index}
                                            reset={resetTrigger}
                                            confValue={rule.config_value}
                                            showUpload
                                            handleDownload= {() => handleFileDownloadbyId(rule.id, "Supplier_Currency_Rules")}
                                      setIsValidationError={setIsValidationError}
                                            isValidationError={isValidationError}
                                />
                                        <Divider sx={{ my: 3 }} />
                                    </>
                                );
                            default:
                                return null;
                            }
                        }
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
                        <PriButton
                            variant="outlined"
                            onClick={handleCancelClick}
                            sx={{mr: 2}}
                        >
                            Cancel
                        </PriButton>
                        <PriButton
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={selectedOption === null || LGInternalUser}
                        >
                            Save
                        </PriButton>

                    </Box>

                </Box>
            </Box>
        </Box>
    </Wrapper>               
}